"use client";

import React, { useState, useEffect } from 'react';
import { Copy, Check, Printer, RotateCcw } from 'lucide-react';

const NATIVE_LANGUAGE_NAMES = {
  'Tamil': 'தமிழ்',
  'Malayalam': 'മലയാളം',
  'Telugu': 'తెలుగు',
  'Hindi': 'हिंदी',
  'Kannada': 'ಕನ್ನಡ',
  'Bengali': 'বাংলা',
  'Punjabi': 'ਪੰਜਾਬੀ',
  'Gujarati': 'ગુજરાતી',
  'Marathi': 'मराठी'
};

export function LyricsDisplay({ englishLyrics, nativeLyrics, translationLyrics, sections, language = 'Tamil' }) {
  // 1. View Mode ('english' or 'native'). Default to 'english'
  const [scriptMode, setScriptMode] = useState('english');
  
  // 2. Global Translation Toggle (default OFF = false, persisted in localStorage)
  const [showTranslation, setShowTranslation] = useState(false);

  // 3. Font Size Scale (default = 1, min = 0.85, max = 1.35)
  const [fontSizeScale, setFontSizeScale] = useState(1);

  // 4. Copy state feedback
  const [copied, setCopied] = useState(false);

  // Load global translation preference on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('lyricsverse_translation_enabled');
      if (saved !== null) {
        setShowTranslation(saved === 'true');
      }
    } catch (e) {
      console.warn("Could not read localStorage translation setting", e);
    }
  }, []);

  // Handle translation toggle and save to localStorage
  const handleToggleTranslation = () => {
    const nextVal = !showTranslation;
    setShowTranslation(nextVal);
    try {
      localStorage.setItem('lyricsverse_translation_enabled', String(nextVal));
    } catch (e) {
      console.warn("Could not write localStorage translation setting", e);
    }
  };

  // Ensure valid initial script mode
  useEffect(() => {
    if (!englishLyrics && nativeLyrics) {
      setScriptMode('native');
    }
  }, [englishLyrics, nativeLyrics]);

  const nativeLabel = NATIVE_LANGUAGE_NAMES[language] || language;

  // Helper to parse double-newline paragraph blocks and filter out dot-only / entity stanzas
  const parseParagraphs = (text) => {
    if (!text) return [];
    return text
      .replace(/&#8230;/g, '')
      .replace(/&hellip;/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .split(/\n\n+/)
      .map(p => {
        const lines = p.split('\n')
          .map(l => l.trim())
          .filter(l => {
            const noDots = l.replace(/[\.\u2026_\-\*~&#8230;;\s]/g, '');
            return noDots.length > 0;
          });
        return lines.join('\n').trim();
      })
      .filter(p => p.length > 0);
  };

  // Build section blocks
  const buildSectionBlocks = () => {
    const nativeParagraphs = parseParagraphs(nativeLyrics);
    const englishParagraphs = parseParagraphs(englishLyrics);
    const translationParagraphs = parseParagraphs(translationLyrics);

    // If sections array has full coverage and matches paragraph lengths or text blocks are missing
    if (Array.isArray(sections) && sections.length > 0 && nativeParagraphs.length === 0 && englishParagraphs.length === 0) {
      return sections.map((sec, idx) => ({
        id: idx,
        sectionName: sec.sectionName || `SECTION ${idx + 1}`,
        nativeLines: sec.lines?.map(l => l.nativeText).join('\n') || '',
        englishLines: sec.lines?.map(l => l.romanText).join('\n') || '',
        translationLines: sec.lines?.map(l => l.englishTranslation).join('\n') || ''
      }));
    }

    const maxLen = Math.max(nativeParagraphs.length, englishParagraphs.length, translationParagraphs.length, 1);

    const blocks = [];
    for (let i = 0; i < maxLen; i++) {
      let engText = englishParagraphs[i] || '';
      let natText = nativeParagraphs[i] || '';
      let transText = translationParagraphs[i] || '';

      // Extract section header if present in any of the three
      let sectionName = null;
      for (const txt of [engText, transText, natText]) {
        const headerMatch = txt.match(/^\[(.*?)\]/);
        if (headerMatch) {
          sectionName = headerMatch[1].toUpperCase();
          break;
        }
      }

      // If sectionName was found, strip the bracket header from all three so it doesn't leak into lyrics lines
      if (sectionName) {
        engText = engText.replace(/^\[.*?\]\n?/, '').trim();
        natText = natText.replace(/^\[.*?\]\n?/, '').trim();
        transText = transText.replace(/^\[.*?\]\n?/, '').trim();
      }

      blocks.push({
        id: i,
        sectionName,
        nativeLines: natText,
        englishLines: engText,
        translationLines: transText
      });
    }
    return blocks;
  };

  const blocks = buildSectionBlocks();
  const hasTranslation = blocks.some(b => b.translationLines.trim().length > 0) || (translationLyrics && translationLyrics.trim().length > 0);

  // Copy full visible lyrics to clipboard
  const handleCopy = () => {
    let fullText = "";
    blocks.forEach(b => {
      if (b.sectionName) fullText += `--- ${b.sectionName} ---\n`;
      const lines = scriptMode === 'native' ? b.nativeLines : b.englishLines;
      if (lines) fullText += lines + "\n";
      if (showTranslation && b.translationLines) {
        fullText += `(Translation: ${b.translationLines})\n`;
      }
      fullText += "\n";
    });

    navigator.clipboard.writeText(fullText.trim()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error("Failed to copy lyrics", err);
    });
  };

  // Print lyrics
  const handlePrint = () => {
    window.print();
  };

  // Font size handlers
  const handleFontMinus = () => setFontSizeScale(prev => Math.max(0.85, prev - 0.1));
  const handleFontReset = () => setFontSizeScale(1);
  const handleFontPlus = () => setFontSizeScale(prev => Math.min(1.35, prev + 0.1));

  // Smart line combiner: if lines are very short, pair adjacent lines for lyrics display
  const formatLyricsText = (text, isTranslation = false) => {
    if (!text) return null;
    const rawLines = text
      .replace(/&#8230;/g, '')
      .replace(/&hellip;/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .split('\n')
      .map(l => l.trim())
      .filter(l => {
        const noDots = l.replace(/[\.\u2026_\-\*~&#8230;;\s]/g, '');
        return noDots.length > 0;
      });
    if (rawLines.length === 0) return null;

    let displayLines = [];
    if (!isTranslation && rawLines.length > 1) {
      const avgLen = rawLines.reduce((acc, l) => acc + l.length, 0) / rawLines.length;
      if (avgLen < 35) {
        for (let i = 0; i < rawLines.length; i += 2) {
          if (i + 1 < rawLines.length) {
            displayLines.push(`${rawLines[i]}  ${rawLines[i + 1]}`);
          } else {
            displayLines.push(rawLines[i]);
          }
        }
      } else {
        displayLines = rawLines;
      }
    } else {
      displayLines = rawLines;
    }

    return displayLines.map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < displayLines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <div className="space-y-6">
      {/* Top Header Controls Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-card/90 border border-border p-3 md:p-4 rounded-3xl shadow-sm backdrop-blur-sm">
        
        {/* Script Selector Pill (English vs Native) */}
        <div className="bg-secondary border border-border/80 rounded-full p-1 inline-flex items-center gap-1">
          {englishLyrics && (
            <button
              type="button"
              onClick={() => setScriptMode('english')}
              aria-label="Show English Lyrics"
              className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-bold transition-all ${
                scriptMode === 'english'
                  ? 'bg-primary text-white shadow-md'
                  : 'text-foreground hover:text-primary hover:bg-background'
              }`}
            >
              English
            </button>
          )}

          {nativeLyrics && (
            <button
              type="button"
              onClick={() => setScriptMode('native')}
              aria-label={`Show ${nativeLabel} Lyrics`}
              className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-bold transition-all ${
                scriptMode === 'native'
                  ? 'bg-primary text-white shadow-md'
                  : 'text-foreground hover:text-primary hover:bg-background'
              }`}
            >
              {nativeLabel}
            </button>
          )}
        </div>

        {/* Right Action Controls (Font, Copy, Print, Translation Toggle) */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          
          {/* Font Size Adjuster Pill */}
          <div className="bg-secondary border border-border/80 rounded-full px-3 py-1 inline-flex items-center gap-2 text-xs font-bold text-foreground">
            <button 
              type="button"
              onClick={handleFontMinus} 
              aria-label="Decrease font size"
              title="Decrease Font Size"
              className="hover:text-primary transition-colors px-1 py-0.5 active:scale-90"
            >
              A-
            </button>
            <button 
              type="button"
              onClick={handleFontReset} 
              aria-label="Reset font size"
              title="Reset Font Size"
              className="hover:text-primary transition-colors p-0.5 active:scale-90"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            <button 
              type="button"
              onClick={handleFontPlus} 
              aria-label="Increase font size"
              title="Increase Font Size"
              className="hover:text-primary transition-colors px-1 py-0.5 active:scale-90"
            >
              A+
            </button>
          </div>

          {/* Copy Button */}
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy lyrics to clipboard"
            className="bg-secondary border border-border/80 hover:border-primary/50 hover:bg-background text-foreground font-semibold px-3.5 py-1.5 rounded-full text-xs md:text-sm inline-flex items-center gap-1.5 transition-all active:scale-95"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-emerald-700 dark:text-emerald-400 font-bold">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5 text-foreground" />
                <span>Copy</span>
              </>
            )}
          </button>

          {/* Print Button */}
          <button
            type="button"
            onClick={handlePrint}
            aria-label="Print lyrics"
            title="Print Lyrics"
            className="bg-secondary border border-border/80 hover:border-primary/50 hover:bg-background text-foreground p-2 rounded-full transition-all active:scale-95"
          >
            <Printer className="w-3.5 h-3.5 text-foreground" />
          </button>

          {/* Translation Toggle Switch */}
          {hasTranslation && (
            <div className="flex items-center gap-2 ml-1 pl-2 border-l border-border/60">
              <span className="text-xs font-bold text-foreground hidden sm:inline">Translation</span>
              <button
                type="button"
                onClick={handleToggleTranslation}
                aria-label="Toggle English Translation"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                  showTranslation ? 'bg-primary' : 'bg-secondary border border-border/80'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showTranslation ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Main Centered Lyrics Display Box */}
      <div className="bg-card border border-border rounded-3xl p-6 sm:p-12 shadow-sm backdrop-blur-sm text-center space-y-8 select-text">
        {blocks.map((block) => {
          const currentText = scriptMode === 'native' ? block.nativeLines : block.englishLines;

          if (!currentText && !block.sectionName) return null;

          return (
            <div key={block.id} className="space-y-3">
              {/* Section Header with Horizontal Lines Flanking Name */}
              {block.sectionName && (
                <div className="flex items-center justify-center gap-4 my-6">
                  <div className="h-[1px] w-12 sm:w-24 bg-gradient-to-r from-transparent to-primary/50" />
                  <span className="text-xs sm:text-sm font-bold uppercase tracking-widest text-primary px-2">
                    {block.sectionName}
                  </span>
                  <div className="h-[1px] w-12 sm:w-24 bg-gradient-to-l from-transparent to-primary/50" />
                </div>
              )}

              {/* Main Lyrics Content (Centered) */}
              {currentText && (
                <p 
                  className={`font-medium text-foreground transition-all duration-200 ${
                    scriptMode === 'native' ? 'leading-loose md:leading-[2.5rem]' : 'leading-relaxed md:leading-loose'
                  }`}
                  style={{ fontSize: `${1.125 * fontSizeScale}rem` }}
                >
                  {formatLyricsText(currentText)}
                </p>
              )}

              {/* Line/Paragraph Translation (Centered below) */}
              {showTranslation && block.translationLines && (
                <p 
                  className="italic text-foreground/80 dark:text-foreground/75 leading-relaxed pt-1 max-w-2xl mx-auto font-medium"
                  style={{ fontSize: `${0.95 * fontSizeScale}rem` }}
                >
                  {formatLyricsText(block.translationLines, true)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
